import React, { useRef, useEffect } from 'react';
import { alpha } from '@material-ui/core/styles/colorManipulator';
import { makeStyles, Theme } from '@material-ui/core/styles';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    position: 'relative',
    margin: '1em',
    height: '4px',
    '& div': {
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
    },
  },
  progress: {
    background: theme.palette.secondary.main,
    right: '100%',
  },
  background: {
    right: 0,
    background: alpha(theme.palette.secondary.main, 0.2),
  },
}));

interface ProgressBarProps {
  duration: number;
  position: number;
  style?: { [key: string]: string };
  progressColor?: string;
  backgroundColor?: string;
}

export default function ProgressBar({ duration, position, style, progressColor, backgroundColor }: ProgressBarProps) {
  const classes = useStyles();
  const progressBarRef = useRef<HTMLDivElement>(null);
  const clampedPosition = Math.min(100, Math.max(0, position));

  useEffect(() => {
    const el = progressBarRef.current;
    if (el) {
      window.requestAnimationFrame(() => {
        // We set these values asynchronously so that the browser can recognize the change in the 'right' value.
        // Without this, the progress bar would instantly snap to the designated position.
        el.style.transition = `right ${duration}s linear`;
        el.style.right = `${String(100 - clampedPosition)}%`;
      });
    }
  }, [duration, clampedPosition]);

  return (
    <div
      className={classes.container}
      style={{ ...style }}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={clampedPosition}
    >
      <div
        className={classes.progress}
        ref={progressBarRef}
        style={{ background: progressColor }}
        data-testid="progress-bar"
      ></div>
      <div className={classes.background} style={{ background: backgroundColor }}></div>
    </div>
  );
}
